<template>
  <div
    :class="['email-component', 'applied-payments-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="applied-payments-wrapper">
      <!-- Section Header -->
      <div class="payments-header" :style="headerStyles">
        <h3 :style="titleStyles">{{ getLabel('title') }}</h3>
      </div>

      <!-- Payments List -->
      <div class="payments-list">
        <div
          v-for="(payment, index) in displayPayments"
          :key="index"
          class="payment-item"
          :style="getPaymentItemStyles(index)"
        >
          <div class="payment-details">
            <div class="payment-method" :style="paymentMethodStyles">
              <span class="payment-description">
                {{ getPaymentDescription(payment) }}
              </span>
            </div>
            <div class="payment-meta" :style="metaStyles">
              <span class="payment-date">{{ formatDate(payment.timestamp) }}</span>
            </div>
          </div>
          <div class="payment-amount" :style="amountStyles">
            {{ formatAmount(payment.amount) }}
          </div>
        </div>
      </div>

      <!-- No Payments Message -->
      <div v-if="displayPayments.length === 0" class="no-payments" :style="noPaymentsStyles">
        {{ getLabel('noPayments') }}
      </div>

      <!-- Divider -->
      <div v-if="componentData.showTotal" class="payments-divider" :style="dividerStyles"></div>

      <!-- Total Applied -->
      <div v-if="componentData.showTotal" class="total-row" :style="totalRowStyles">
        <span class="total-label">{{ getLabel('totalApplied') }}</span>
        <span class="total-value">{{ totalAppliedAmount }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppliedPaymentsComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      // Sample payments data - in production this would come from the API
      samplePayments: [
        {
          type: 'card',
          card_type: 'visa',
          cardholder_first_name: 'John',
          cardholder_last_name: 'Doe',
          last_four: '4242',
          amount: 100.00,
          timestamp: '2025-01-15T14:30:00',
          gateway_transaction_id: 'ch_1234567890'
        },
        {
          type: 'card',
          card_type: 'mastercard',
          cardholder_first_name: 'Jane',
          cardholder_last_name: 'Smith',
          last_four: '5555',
          amount: 82.70,
          timestamp: '2025-01-15T14:35:00',
          gateway_transaction_id: 'ch_0987654321'
        }
      ],

      labels: {
        english: {
          title: 'Applied Payments',
          noPayments: 'No payments have been applied yet',
          totalApplied: 'Total Applied',
          pending: 'Pending',
          completed: 'Completed',
          failed: 'Failed',
          refunded: 'Refunded'
        },
        spanish: {
          title: 'Pagos Aplicados',
          noPayments: 'No se han aplicado pagos todavía',
          totalApplied: 'Total Aplicado',
          pending: 'Pendiente',
          completed: 'Completado',
          failed: 'Fallido',
          refunded: 'Reembolsado'
        },
        french: {
          title: 'Paiements Appliqués',
          noPayments: 'Aucun paiement n\'a encore été appliqué',
          totalApplied: 'Total Appliqué',
          pending: 'En attente',
          completed: 'Terminé',
          failed: 'Échoué',
          refunded: 'Remboursé'
        },
        swedish: {
          title: 'Betalningar',
          noPayments: 'Inga betalningar har tillämpats än',
          totalApplied: 'Totalt Tillämpat',
          pending: 'Väntar',
          completed: 'Slutförd',
          failed: 'Misslyckades',
          refunded: 'Återbetalad'
        },
        danish: {
          title: 'Betalinger',
          noPayments: 'Ingen betalinger er anvendt endnu',
          totalApplied: 'Anvendt i alt',
          pending: 'Afventer',
          completed: 'Gennemført',
          failed: 'Mislykkedes',
          refunded: 'Refunderet'
        },
        greek: {
          title: 'Εφαρμοσμένες Πληρωμές',
          noPayments: 'Δεν έχουν εφαρμοστεί πληρωμές ακόμα',
          totalApplied: 'Σύνολο Εφαρμοσμένο',
          pending: 'Εκκρεμεί',
          completed: 'Ολοκληρώθηκε',
          failed: 'Απέτυχε',
          refunded: 'Επιστράφηκε'
        }
      },
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    displayPayments() {
      // In production, this would use actual transaction payments
      // For now, show sample payments or empty based on configuration
      return this.componentData.showSampleData !== false ? this.samplePayments : []
    },

    totalAppliedAmount() {
      const total = this.displayPayments.reduce((sum, payment) => sum + payment.amount, 0)
      return this.formatAmount(total)
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingRight || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingLeft || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    headerStyles() {
      const styles = this.componentData.styles || {}
      return {
        marginBottom: '16px',
        paddingBottom: '8px',
        borderBottom: styles.showHeaderBorder !== false ? `1px solid ${styles.dividerColor || '#e5e7eb'}` : 'none'
      }
    },

    titleStyles() {
      const styles = this.componentData.styles || {}
      return {
        margin: '0',
        fontSize: `${styles.titleFontSize || '16'}px`,
        fontWeight: '600',
        color: styles.titleColor || '#111827',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    paymentMethodStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: `${styles.fontSize || '14'}px`,
        color: styles.textColor || '#333333',
        fontWeight: '500'
      }
    },

    metaStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        gap: '12px',
        fontSize: `${styles.metaFontSize || '12'}px`,
        color: styles.metaColor || '#666666',
        marginTop: '4px'
      }
    },

    amountStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.amountFontSize || '15'}px`,
        fontWeight: '600',
        color: styles.amountColor || '#111827',
        whiteSpace: 'nowrap'
      }
    },

    dividerStyles() {
      const styles = this.componentData.styles || {}
      return {
        borderTop: `1px solid ${styles.dividerColor || '#e5e7eb'}`,
        marginTop: '16px',
        marginBottom: '12px'
      }
    },

    totalRowStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: `${styles.totalFontSize || '16'}px`,
        fontWeight: '700',
        color: styles.totalColor || '#111827',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    noPaymentsStyles() {
      const styles = this.componentData.styles || {}
      return {
        textAlign: 'center',
        padding: '24px',
        fontSize: `${styles.fontSize || '14'}px`,
        color: styles.metaColor || '#666666',
        fontStyle: 'italic'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    },

    getPaymentDescription(payment) {
      if (payment.type === 'card') {
        const cardType = payment.card_type ? payment.card_type.charAt(0).toUpperCase() + payment.card_type.slice(1) : 'Card'
        const name = payment.cardholder_first_name && payment.cardholder_last_name ?
          `${payment.cardholder_first_name} ${payment.cardholder_last_name}` : ''
        const lastFour = payment.last_four ? `••••${payment.last_four}` : ''

        return `${cardType} ${lastFour}${name ? ` - ${name}` : ''}`
      }
      return payment.type.charAt(0).toUpperCase() + payment.type.slice(1)
    },

    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleDateString(this.getLocale(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    formatAmount(amount) {
      const currencySymbol = this.componentData.currencySymbol || '$'
      const formatted = parseFloat(amount).toFixed(2)

      // Format based on language
      if (this.currentLanguage === 'french' || this.currentLanguage === 'spanish') {
        const europeanFormatted = formatted.replace('.', ',')
        return this.currentLanguage === 'french' ?
          `${europeanFormatted} ${currencySymbol}` :
          `${currencySymbol}${europeanFormatted}`
      } else if (this.currentLanguage === 'swedish' || this.currentLanguage === 'greek' || this.currentLanguage === 'danish') {
        const europeanFormatted = formatted.replace('.', ',')
        return `${europeanFormatted} ${currencySymbol}`
      }

      return `${currencySymbol}${formatted}`
    },

    getLocale() {
      const localeMap = {
        english: 'en-US',
        spanish: 'es-ES',
        french: 'fr-FR',
        swedish: 'sv-SE',
        greek: 'el-GR',
        danish: 'da-DK'
      }
      return localeMap[this.currentLanguage] || 'en-US'
    },

    getPaymentItemStyles(index) {
      const styles = this.componentData.styles || {}
      const isLast = index === this.displayPayments.length - 1

      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '12px',
        paddingBottom: '12px',
        borderBottom: !isLast && styles.showItemDividers !== false ?
          `1px solid ${styles.itemDividerColor || '#f3f4f6'}` : 'none'
      }
    }
  }
}
</script>

<style scoped>
.applied-payments-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.applied-payments-component:hover,
.applied-payments-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.applied-payments-wrapper {
  width: 100%;
}

.payments-list {
  width: 100%;
}

.payment-item {
  width: 100%;
  transition: background-color 0.2s ease;
}

.payment-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
  margin: 0 -8px;
  padding-left: 8px;
  padding-right: 8px;
}

.payment-details {
  flex: 1;
  min-width: 0;
}

.payment-method {
  margin-bottom: 4px;
}

.payment-icon {
  display: inline-block;
  vertical-align: middle;
}

.payment-description {
  vertical-align: middle;
}

.payment-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.payment-date {
  margin-right: 12px;
}

.payment-amount {
  flex-shrink: 0;
  margin-left: 16px;
}

.total-label {
  flex: 1;
  text-align: left;
}

.total-value {
  text-align: right;
  white-space: nowrap;
}

.no-payments {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
}

@media (max-width: 600px) {
  .payment-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .payment-amount {
    margin-left: 0;
    margin-top: 8px;
    font-size: 16px !important;
  }

  .payment-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .payment-date {
    margin-right: 0;
  }
}
</style>
